import { defineStore } from "pinia";

import type {
  SimpleOrderType,
  AccountType,
  PaymentMethod,
  AccountPaymentMethods,
  // SimpleOrderAddress,
  SimpleOrderAddressUpdated,
} from "@/types";

interface State {
  simpleOrderPayload: SimpleOrderType;
  loading: boolean;
  submissionError: string | null;
  orderConfirmation: any | null;
  selectedPaymentMethod: AccountPaymentMethods | null;
  paymentSession: {
    tokenData: {
      paymentToken: string | null;
      tokenExpiry: number | null; // Unix timestamp
      cardType: string | null;
      lastFour: string | null;
      expirationDate: Date | null;
    } | null;
    validationState: {
      isValid: boolean;
      lastValidated: number | null; // Unix timestamp
      errors: string[];
    };
    cardDetails: {
      cardName: string | null;
      friendlyName: string | null;
      isDefault: boolean;
      savePayment: boolean;
    } | null;
  };
}

const defaultAddress: SimpleOrderAddressUpdated = {
  id: 0,
};

// 233333  -- Error message: "Product not found"
const initialSimpleOrderState: SimpleOrderType = {
  metadata: {
    request_time: new Date().toISOString(),
    requestType: "simpleOrder",
  },
  account: {
    salutation: "",
    firstName: "",
    middlename: "",
    lastName: "",
    accountType: "Residential",
    isPriorityAssistance: false,
    dateOfBirth: "",
    phonePrimary: "",
    phoneSecondary: "",
    phoneFax: "",
    emailPrimary: "",
    emailOptout: false,
    isEmailpostal: "manual",
    invoiceDesign: "OnePass",
    invoiceEmailAddress: "",
    customDescription: "Online GO Commerce",
    externalIdentifier: "",
    domesticViolence: false,
    hardship: false,
    partnerCode: "DigitalSale",
    paymentMethod: {
      creditCard: {
        providerToken: "",
        providerClientId: "",
      },
    },
    shippingAddress: defaultAddress,
  },
  shipmentMethod: "STANDARD",
  orderLines: {},
};

// Transform API address to server-expected format for simpleOrder submission only
const transformAddressForServer = (
  apiAddress: any,
  addressType: string = "billing"
) => {
  console.log("Transforming address for store:", {
    apiAddress,
    addressType,
  });
  if (!apiAddress) return null;

  // Extract state abbreviation from API format
  const stateValue =
    typeof apiAddress.state === "object"
      ? apiAddress.state.abbreviation
      : apiAddress.state;

  return {
    name: addressType, // billing, postal, residential
    id: apiAddress.id,
    address1: apiAddress.addressLine1,
    address2: apiAddress.addressLine2 || "",
    city: apiAddress.city,
    state: stateValue, // String abbreviation only
    postCode: apiAddress.postalCode, // Server expects postCode not postalCode
    customerId: apiAddress.billingId,
    flatUnitNumber: apiAddress.flatUnitNumber || "",
    flatUnitType: apiAddress.flatUnitType || "",
    streetNumber: apiAddress.streetNumber || "",
    streetName: apiAddress.streetName || "",
    streetType: apiAddress.streetType || "",
    // listingPreference only for activation journey
  };
};

export const useSimpleOrderStore = defineStore({
  id: "simpleOrderStore",
  state: (): State => ({
    simpleOrderPayload: initialSimpleOrderState,
    loading: false,
    submissionError: null,
    orderConfirmation: null,
    selectedPaymentMethod: null,
    paymentSession: {
      tokenData: null,
      validationState: {
        isValid: false,
        lastValidated: null,
        errors: [],
      },
      cardDetails: null,
    },
  }),

  // Add persistence to maintain checkout state across browser refreshes
  persist: process.client
    ? ({
        storage: localStorage,
        key: "simpleOrderStore",
        paths: [
          "simpleOrderPayload.account.salutation",
          "simpleOrderPayload.account.firstName",
          "simpleOrderPayload.account.lastName",
          "simpleOrderPayload.account.dateOfBirth",
          "simpleOrderPayload.account.emailPrimary",
          "simpleOrderPayload.account.invoiceEmailAddress",
          "simpleOrderPayload.account.shippingAddress",
          "simpleOrderPayload.shipmentMethod",
          "simpleOrderPayload.account.paymentMethod",
          "selectedPaymentMethod",
          "paymentSession.tokenData",
          "paymentSession.validationState",
          "paymentSession.cardDetails",
        ],
      } as any)
    : false,

  actions: {
    clearSimpleOrderPayload() {
      this.simpleOrderPayload = initialSimpleOrderState;
      this.submissionError = null;
      this.selectedPaymentMethod = null;
      this.paymentSession = {
        tokenData: null,
        validationState: {
          isValid: false,
          lastValidated: null,
          errors: [],
        },
        cardDetails: null,
      };
    },
    clearOrderConfirmation() {
      this.orderConfirmation = null;
    },
    updateAccount(account: AccountType) {
      this.simpleOrderPayload.account.salutation = account.salutation;
      this.simpleOrderPayload.account.firstName = account.firstName;
      this.simpleOrderPayload.account.lastName = account.lastName;
      this.simpleOrderPayload.account.dateOfBirth = account.dateOfBirth;
      this.simpleOrderPayload.account.emailPrimary = account.emailPrimary;
      this.simpleOrderPayload.account.invoiceEmailAddress =
        account.emailPrimary;
    },
    updateShippingAddress(shippingAddressId: number, address: any) {
      // Transform API address to server format before storing
      // const serverAddress = transformAddressForServer(
      this.simpleOrderPayload.account.addresses = address;
      this.simpleOrderPayload.account.shippingAddress = {
        id: shippingAddressId,
      };
    },
    updateShippingMethod({ shipmentMethod }: { shipmentMethod: string }) {
      this.simpleOrderPayload.shipmentMethod = shipmentMethod;
    },
    updatePaymentMethod(paymentMethod: PaymentMethod) {
      this.simpleOrderPayload.account.paymentMethod = paymentMethod;
    },

    setSelectedPaymentMethod(paymentMethod: AccountPaymentMethods | null) {
      this.selectedPaymentMethod = paymentMethod;
    },
    updatePaymentSessionToken(tokenData: {
      paymentToken: string | null;
      tokenExpiry: number | null; // Unix timestamp
      cardType: string | null;
      lastFour: string | null;
      expirationDate: Date | null;
    }) {
      this.paymentSession.tokenData = tokenData;
    },
    updatePaymentSessionValidation(validationState: {
      isValid: boolean;
      lastValidated: number | null; // Unix timestamp
      errors: string[];
    }) {
      this.paymentSession.validationState = validationState;
    },
    updatePaymentSessionCardDetails(cardDetails: {
      cardName: string | null;
      friendlyName: string | null;
      isDefault: boolean;
      savePayment: boolean;
    }) {
      this.paymentSession.cardDetails = cardDetails;
    },

    setCompletePaymentSession(data: {
      paymentToken: string;
      cardType: string;
      lastFour: string;
      expirationDate: Date;
      cardName: string;
      friendlyName?: string;
      isDefault?: boolean;
      savePayment?: boolean;
      tokenExpiryMinutes?: number;
    }) {
      const now = Date.now();
      const tokenExpiry = now + (data.tokenExpiryMinutes || 30) * 60 * 1000; // Default 30 minutes

      // Update token data
      this.updatePaymentSessionToken({
        paymentToken: data.paymentToken,
        tokenExpiry,
        cardType: data.cardType,
        lastFour: data.lastFour,
        expirationDate: data.expirationDate,
      });

      // Update card details
      this.updatePaymentSessionCardDetails({
        cardName: data.cardName,
        friendlyName: data.friendlyName || data.cardName,
        isDefault: data.isDefault || false,
        savePayment: data.savePayment || true,
      });

      // Update validation state
      this.updatePaymentSessionValidation({
        isValid: true,
        lastValidated: now,
        errors: [],
      });

      // Sync with the main payment method
      this.updatePaymentMethod({
        creditCard: {
          providerToken: data.paymentToken,
          providerClientId: null,
        },
      });

      console.log("Payment session updated:", {
        token: data.paymentToken.substring(0, 8) + "...",
        expiry: new Date(tokenExpiry).toISOString(),
        cardType: data.cardType,
        lastFour: data.lastFour,
      });
    },

    clearPaymentSession() {
      this.paymentSession = {
        tokenData: null,
        validationState: {
          isValid: false,
          lastValidated: null,
          errors: [],
        },
        cardDetails: null,
      };
      this.selectedPaymentMethod = null;
      delete this.simpleOrderPayload.account.paymentMethod;
    },

    validatePaymentSession(): boolean {
      const { tokenData, validationState } = this.paymentSession;

      // Check if we have token data
      if (!tokenData?.paymentToken) {
        this.updatePaymentSessionValidation({
          isValid: false,
          lastValidated: Date.now(),
          errors: ["No payment token available"],
        });
        return false;
      }

      // Check token expiry
      if (tokenData.tokenExpiry && Date.now() > tokenData.tokenExpiry) {
        this.updatePaymentSessionValidation({
          isValid: false,
          lastValidated: Date.now(),
          errors: ["Payment token has expired"],
        });
        return false;
      }

      // Check required fields
      const missingFields = [];
      if (!tokenData.cardType) missingFields.push("cardType");
      if (!tokenData.lastFour) missingFields.push("lastFour");
      if (!this.paymentSession.cardDetails?.cardName)
        missingFields.push("cardName");

      if (missingFields.length > 0) {
        this.updatePaymentSessionValidation({
          isValid: false,
          lastValidated: Date.now(),
          errors: [`Missing required fields: ${missingFields.join(", ")}`],
        });
        return false;
      }

      // All checks passed
      this.updatePaymentSessionValidation({
        isValid: true,
        lastValidated: Date.now(),
        errors: [],
      });
      return true;
    },

    refreshPaymentTokenExpiry(additionalMinutes: number = 30) {
      if (this.paymentSession.tokenData?.paymentToken) {
        const newExpiry = Date.now() + additionalMinutes * 60 * 1000;
        this.updatePaymentSessionToken({
          ...this.paymentSession.tokenData,
          tokenExpiry: newExpiry,
        });
        console.log(
          `Payment token expiry extended by ${additionalMinutes} minutes`
        );
      }
    },
  },
  getters: {
    // Get address in server-compatible format
    serverCompatibleAddress: (state) => {
      const billingAddress =
        state.simpleOrderPayload?.account?.addresses?.billing;
      return transformAddressForServer(billingAddress, "billing");
    },

    // Check if we have a valid address selected
    hasValidAddress: (state) => {
      const billingAddress = state.simpleOrderPayload?.account?.addresses
        ?.billing as any;
      return (
        billingAddress &&
        billingAddress.id &&
        (billingAddress.postalCode || billingAddress.postcode)
      );
    },

    // Payment session getters
    isPaymentTokenValid: (state) => {
      const { tokenData } = state.paymentSession;
      if (!tokenData?.paymentToken) return false;

      // Check token expiry
      if (tokenData.tokenExpiry && Date.now() > tokenData.tokenExpiry)
        return false;

      return true;
    },

    paymentTokenTimeRemaining: (state) => {
      const { tokenData } = state.paymentSession;
      if (!tokenData?.tokenExpiry) return null;

      const remaining = tokenData.tokenExpiry - Date.now();
      return remaining > 0 ? remaining : 0;
    },

    isPaymentSessionComplete: (state) => {
      const { tokenData, cardDetails, validationState } = state.paymentSession;

      return !!(
        tokenData?.paymentToken &&
        tokenData?.cardType &&
        tokenData?.lastFour &&
        cardDetails?.cardName &&
        validationState.isValid
      );
    },

    paymentSessionSummary: (state) => {
      const { tokenData, cardDetails, validationState } = state.paymentSession;

      if (!tokenData?.paymentToken) {
        return {
          status: "no_token",
          message: "No payment token available",
          canProceed: false,
        };
      }

      if (tokenData.tokenExpiry && Date.now() > tokenData.tokenExpiry) {
        return {
          status: "expired",
          message: "Payment token has expired",
          canProceed: false,
        };
      }

      if (!validationState.isValid) {
        return {
          status: "invalid",
          message:
            validationState.errors.join(", ") || "Payment validation failed",
          canProceed: false,
        };
      }

      return {
        status: "ready",
        message: `${cardDetails?.cardName || "Unknown"} ending in ${tokenData.lastFour}`,
        canProceed: true,
        cardType: tokenData.cardType,
        lastFour: tokenData.lastFour,
        cardName: cardDetails?.cardName,
        isDefault: cardDetails?.isDefault || false,
      };
    },

    // Enhanced payment readiness check
    isReadyForPayment: (state) => {
      // Check if we have either existing payment method or valid payment session
      const hasExistingPayment = !!state.selectedPaymentMethod;
      const hasValidSession = !!(
        state.paymentSession.tokenData?.paymentToken &&
        state.paymentSession.validationState.isValid &&
        (!state.paymentSession.tokenData.tokenExpiry ||
          Date.now() < state.paymentSession.tokenData.tokenExpiry)
      );

      return hasExistingPayment || hasValidSession;
    },

    // Get the current active payment method (existing or new)
    activePaymentMethod: (state) => {
      // Priority: existing selected payment method
      if (state.selectedPaymentMethod) {
        return {
          type: "existing",
          method: state.selectedPaymentMethod,
        };
      }

      // Fallback: payment session data
      if (
        state.paymentSession.tokenData?.paymentToken &&
        state.paymentSession.validationState.isValid
      ) {
        return {
          type: "new",
          method: {
            paymentToken: state.paymentSession.tokenData.paymentToken,
            cardType: state.paymentSession.tokenData.cardType,
            lastFour: state.paymentSession.tokenData.lastFour,
            cardName: state.paymentSession.cardDetails?.cardName,
            isDefault: state.paymentSession.cardDetails?.isDefault || false,
          },
        };
      }

      return null;
    },
  },
});
